import { useCallback, useEffect, useRef, useState } from "react";
import { useAppRuntime } from "../../../providers/AppRuntimeProvider";
import type {
  AiProvider,
  AiModel,
  CatalogResponse,
  ModelDefault,
  SubscriptionStatus,
} from "../types";

export interface CreateProviderInput {
  readonly id?: string;
  readonly name: string;
  readonly base_url: string;
  readonly api_key?: string;
}

/**
 * INV-MA-5: "Add Provider" branches on presence in `list_providers` —
 * a seeded/known provider is updated in place, a new custom OpenAI-compatible
 * one is created (base_url required).
 */
export interface ConnectProviderInput {
  readonly id: string;
  readonly name: string;
  readonly base_url: string;
  readonly api_key?: string;
}

/** INV-MA-3: enable a catalog model = materialize an enabled chat row. */
export interface EnableModelInput {
  readonly provider_id: string;
  readonly model_id: string;
}

/** Manual model creation for non-catalog providers (Codex subscription). */
export interface CreateModelInput {
  readonly provider_id: string;
  readonly model_id: string;
  readonly name: string;
  readonly capability?: "chat" | "reasoning" | "embedding";
}

export interface UseAiModelsResult {
  readonly providers: readonly AiProvider[];
  readonly models: readonly AiModel[];
  readonly defaults: readonly ModelDefault[];
  readonly loading: boolean;
  /** Set when the mount load RPCs fail; providers/models are left as-is. */
  readonly loadError: string | null;
  readonly updateProvider: (
    id: string,
    updates: { api_key?: string; base_url?: string; enabled?: boolean },
  ) => Promise<void>;
  readonly createProvider: (data: CreateProviderInput) => Promise<AiProvider>;
  /** INV-MA-5: connect a provider — update if it already exists, else create. */
  readonly connectProvider: (data: ConnectProviderInput) => Promise<AiProvider>;
  readonly deleteProvider: (id: string) => Promise<void>;
  /** INV-MA-3: enable a catalog model (materialize + seed price), append it. */
  readonly enableModel: (data: EnableModelInput) => Promise<AiModel>;
  /** Manually create a model for a provider not in the models.dev catalog
   *  (e.g. the Codex subscription: model IDs are account-specific). */
  readonly createModel: (data: CreateModelInput) => Promise<AiModel>;
  readonly deleteModel: (id: string) => Promise<void>;
  readonly updateModel: (
    id: string,
    updates: { enabled?: boolean; config_json?: string },
  ) => Promise<void>;
  readonly setDefault: (capability: string, modelId: string) => Promise<void>;
  /** INV-MA-1: search the flat models.dev catalog for the model picker. */
  readonly catalogModels: (search: string) => Promise<CatalogResponse>;
  /** INV-CAT-9: loaded lazily by the subscription card, not on mount. */
  readonly subscriptionStatus: SubscriptionStatus | null;
  readonly subscriptionStatusError: string | null;
  readonly loadSubscriptionStatus: () => Promise<void>;
  readonly reload: () => Promise<void>;
}

export function useAiModels(): UseAiModelsResult {
  const runtime = useAppRuntime();
  const [providers, setProviders] = useState<AiProvider[]>([]);
  const [models, setModels] = useState<AiModel[]>([]);
  const [defaults, setDefaults] = useState<ModelDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [subscriptionStatusError, setSubscriptionStatusError] = useState<string | null>(null);
  const loadedRef = useRef(false);
  // INV-MA-5: connectProvider needs the CURRENT provider set to branch
  // update-vs-create without stale-closure captures.
  const providersRef = useRef<readonly AiProvider[]>([]);
  useEffect(() => {
    providersRef.current = providers;
  }, [providers]);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [p, m, d] = await Promise.all([
        runtime.transport.rpc<AiProvider[]>("ai_models.list_providers"),
        runtime.transport.rpc<AiModel[]>("ai_models.list_models", { capability: "chat" }),
        runtime.transport.rpc<ModelDefault[]>("ai_models.get_defaults"),
      ]);
      setProviders(p);
      setModels(m);
      setDefaults(d);
    } catch (err) {
      // Surface the failure; leave providers/models/defaults untouched so the
      // panel does not render as if the catalog were empty (INV-CAT-8).
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    void load();
  }, [load]);

  const updateProvider = useCallback(
    async (
      id: string,
      updates: { api_key?: string; base_url?: string; enabled?: boolean },
    ): Promise<void> => {
      const updated = await runtime.transport.rpc<AiProvider>("ai_models.update_provider", {
        id,
        ...updates,
      });
      setProviders((prev) => prev.map((p) => (p.id === id ? updated : p)));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const createProvider = useCallback(
    async (data: CreateProviderInput): Promise<AiProvider> => {
      const provider = await runtime.transport.rpc<AiProvider>(
        "ai_models.create_provider",
        { ...data },
      );
      setProviders((prev) => [provider, ...prev]);
      return provider;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const connectProvider = useCallback(
    async (data: ConnectProviderInput): Promise<AiProvider> => {
      const existing = providersRef.current.find((p) => p.id === data.id);
      if (existing) {
        // Seeded/known provider (anthropic/openai/openrouter/…): set the key
        // and enable it in place.
        const updated = await runtime.transport.rpc<AiProvider>("ai_models.update_provider", {
          id: data.id,
          ...(data.api_key ? { api_key: data.api_key } : {}),
          enabled: true,
        });
        setProviders((prev) => prev.map((p) => (p.id === data.id ? updated : p)));
        return updated;
      }
      // New custom OpenAI-compatible provider: create it (base_url required).
      const created = await runtime.transport.rpc<AiProvider>("ai_models.create_provider", {
        id: data.id,
        name: data.name,
        base_url: data.base_url,
        ...(data.api_key ? { api_key: data.api_key } : {}),
      });
      setProviders((prev) => [created, ...prev]);
      return created;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deleteProvider = useCallback(async (id: string): Promise<void> => {
    await runtime.transport.rpc("ai_models.delete_provider", { id });
    setProviders((prev) => prev.filter((p) => p.id !== id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableModel = useCallback(
    async (data: EnableModelInput): Promise<AiModel> => {
      const model = await runtime.transport.rpc<AiModel>("ai_models.enable_model", { ...data });
      // Replace any prior row for the same id (idempotent re-enable), else append.
      setModels((prev) => [...prev.filter((m) => m.id !== model.id), model]);
      return model;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const createModel = useCallback(
    async (data: CreateModelInput): Promise<AiModel> => {
      const model = await runtime.transport.rpc<AiModel>("ai_models.create_model", {
        capability: "chat",
        ...data,
      });
      setModels((prev) => [...prev.filter((m) => m.id !== model.id), model]);
      return model;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const deleteModel = useCallback(async (id: string): Promise<void> => {
    await runtime.transport.rpc("ai_models.delete_model", { id });
    setModels((prev) => prev.filter((m) => m.id !== id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateModel = useCallback(
    async (
      id: string,
      updates: { enabled?: boolean; config_json?: string },
    ): Promise<void> => {
      const updated = await runtime.transport.rpc<AiModel>("ai_models.update_model", {
        id,
        ...updates,
      });
      setModels((prev) => prev.map((m) => (m.id === id ? updated : m)));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const setDefault = useCallback(
    async (capability: string, modelId: string): Promise<void> => {
      const updated = await runtime.transport.rpc<ModelDefault>("ai_models.set_default", {
        capability,
        model_id: modelId,
      });
      setDefaults((prev) => {
        const filtered = prev.filter((d) => d.capability !== capability);
        return [...filtered, updated];
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const catalogModels = useCallback(
    async (search: string): Promise<CatalogResponse> => {
      return runtime.transport.rpc<CatalogResponse>("ai_models.catalog_models", { search });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const loadSubscriptionStatus = useCallback(async (): Promise<void> => {
    try {
      const status = await runtime.transport.rpc<SubscriptionStatus>(
        "ai_models.subscription_status",
        {},
      );
      setSubscriptionStatus(status);
      setSubscriptionStatusError(null);
    } catch (err) {
      // Sidecar unreachable ⇒ we cannot tell connected from disconnected.
      // Do NOT fabricate a status: leave it null and surface the error so the
      // card shows "unreachable", not a misleading "run codex login" hint.
      setSubscriptionStatus(null);
      setSubscriptionStatusError(err instanceof Error ? err.message : String(err));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    providers,
    models,
    defaults,
    loading,
    loadError,
    updateProvider,
    createProvider,
    connectProvider,
    deleteProvider,
    enableModel,
    createModel,
    deleteModel,
    updateModel,
    setDefault,
    catalogModels,
    subscriptionStatus,
    subscriptionStatusError,
    loadSubscriptionStatus,
    reload: load,
  };
}
